// ID checks — the staff document check, and the rule that decides whether a venue needs one
// (spec/07 §10, §12; spec/05 §3.2 check 4; spec/04 §2, §13; spec/13 §8; spec/20 §6; F-CMP-02, F-CMP-03).
//
//   POST /v1/organizations/{o}/players/{p}/id-checks                     players:id_check, Idempotency-Key
//   GET  /v1/organizations/{o}/players/{p}/id-checks                     players:id_check
//   GET  /v1/organizations/{o}/players/{p}/id-checks/{idCheckId}/photo   players:id_check, one audit row a call
//   GET  /v1/files/{fileId}?organizationId=…&expires=…&signature=…       the signature is the authority
//
// **What a staff document check is.** A member at the desk looks at a physical ID and records the
// document's type and its **last four characters** — nothing more of the number is ever sent, and
// the body is strict, so a request that carries a `documentNumber` is refused rather than
// quietly ignored. An optional photo of the document may go with it. The check counts **only for
// the organisation that made it** (spec/07 §10): another organisation never sees it, and it never
// satisfies another organisation's rule.
//
// **The photo** is the most sensitive thing a venue ever stores, so:
//
//   - it travels in the create request itself, base64 in the JSON body, and the server — not the
//     browser — puts it in the `private` bucket, **encrypted** under a key that is in neither the
//     database nor the bucket (spec/20 §9). The `private` bucket has no CORS rule and never will
//     (spec/03 §7.4), so a browser could not PUT to it anyway; and one request is one transaction,
//     one audit row and one `Idempotency-Key`, with no half-made check left behind by a lost upload;
//   - it is read back only through `…/id-checks/{idCheckId}/photo`, which needs `players:id_check`,
//     writes one audit row every time it is called, and answers a **signed URL that lives 60
//     seconds** — `GET /v1/files/{fileId}?…` — which serves the decrypted image with
//     `Cache-Control: no-store`;
//   - it is deleted by the worker's retention job once it is `compliance.idPhotoRetentionDays`
//     old (default 30), and the check itself stays (spec/04 §13).
//
// It is not a `File` (`routes/files.ts`) and never appears in `GET …/files/{fileId}`: `File` and
// `FilePurpose` describe public media, and a new value in a response enum would break the station's
// generated C# enums (spec/08 §6).
//
// **Idempotency** (spec/08 §2.2, spec/04 §1 rule 5). The create needs an `Idempotency-Key`. Sent
// again with the same key and the same body, it answers the check it already made — `201`, the same
// body, `Idempotent-Replayed: true` — and records nothing twice. The same key with a different body
// is `422 idempotency-key-reused`. The key is kept on the check, per organisation.
//
// **The rule** (F-CMP-03) is a setting, `compliance.idCheckRequired` — `never` (the default),
// `before_first_session` or `always_verified` — with `compliance.acceptedIdMethods` and
// `compliance.idCheckValidityMonths` beside it (spec/07 §12, spec/05 §13). It is enforced by the
// server at session start as check 4, `id-check-required` (spec/05 §3.2); the kiosk reacts, it
// never decides.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { FileIdSchema, IdCheckIdSchema, OrganizationIdSchema, PlayerIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { PLAYERS_ID_CHECK } from "../permissions/index.js";
import { PageQuerySchema, paginated } from "../primitives/page.js";
import { IdempotencyKeySchema, IsoDateTimeSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema } from "../primitives/scope.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { ImageContentTypeSchema } from "./files.js";
// -------------------------------------------------------------------------------------------------
// The rule and the methods (spec/07 §10, §12; the settings' value types)
// -------------------------------------------------------------------------------------------------
/**
 * `compliance.idCheckRequired` (spec/07 §10, spec/05 §13):
 *
 *   never                 no check
 *   before_first_session  verified once, by a method the organisation accepts, before the first
 *                         session at its venues — and never again
 *   always_verified       the same, and verified again when the verification is older than
 *                         `compliance.idCheckValidityMonths`
 */
export const ID_CHECK_RULES = ['never', 'before_first_session', 'always_verified'];
export const IdCheckRuleSchema = z.enum(ID_CHECK_RULES).openapi('IdCheckRule');
/**
 * How a player was verified (spec/07 §10): `digital` — DigiLocker or Aadhaar offline e-KYC through a
 * licensed provider (F-CMP-01, M5) — or `staff_document`, a member looking at a physical ID. Both are
 * listed from the start, so the day digital verification lands no client meets a value it does not
 * know.
 */
export const ID_CHECK_METHODS = ['digital', 'staff_document'];
export const IdCheckMethodSchema = z.enum(ID_CHECK_METHODS).openapi('IdCheckMethod');
/**
 * What a staff member may record they looked at. India's documents that show a photo and a date of
 * birth, and `other` for anything else — a foreign passport is a `passport`; an employer's card is
 * `other`, and the venue's own policy decides whether it takes one.
 */
export const ID_DOCUMENT_TYPES = ['aadhaar', 'pan', 'passport', 'driving_licence', 'voter_id', 'other'];
export const IdDocumentTypeSchema = z.enum(ID_DOCUMENT_TYPES).openapi('IdDocumentType');
/**
 * The last four characters of the document's number, and nothing more (spec/07 §10). Letters and
 * digits only — a PAN ends in a letter, a passport number may — and the server stores them upper-cased.
 */
export const DOCUMENT_LAST4_PATTERN = /^[A-Za-z0-9]{4}$/;
export const DocumentLast4Schema = z
    .string()
    .regex(DOCUMENT_LAST4_PATTERN, 'Exactly the last four letters or digits of the number')
    .openapi('DocumentLast4', {
    description: 'The last four characters of the document number. The full number is never sent or stored.',
    example: '4821',
});
// -------------------------------------------------------------------------------------------------
// The photo
// -------------------------------------------------------------------------------------------------
/**
 * 5 MB. A photo of a card needs far less; the console and the staff app scale a camera frame down
 * before they send it, and anything this size is a photo nobody meant to keep.
 */
export const MAX_ID_PHOTO_BYTES = 5 * 1024 * 1024;
/** The longest base64 a photo of `MAX_ID_PHOTO_BYTES` encodes to. */
export const MAX_ID_PHOTO_BASE64_LENGTH = Math.ceil(MAX_ID_PHOTO_BYTES / 3) * 4;
/** How long a signed photo URL lives. Long enough to load an image; useless to anyone it is pasted to. */
export const ID_PHOTO_URL_TTL_SECONDS = 60;
export const IdCheckPhotoUploadSchema = z
    .object({
    contentType: ImageContentTypeSchema,
    /** The image, base64 (RFC 4648 §4, with padding). Its first bytes must be the declared type's. */
    data: z.base64().min(1).max(MAX_ID_PHOTO_BASE64_LENGTH),
})
    .strict()
    .openapi('IdCheckPhotoUpload', {
    description: `A photo of the document, at most ${MAX_ID_PHOTO_BYTES} bytes before encoding.`,
});
export const RecordIdCheckSchema = z
    .object({
    documentType: IdDocumentTypeSchema,
    documentLast4: DocumentLast4Schema,
    /** Optional. Kept `compliance.idPhotoRetentionDays` (default 30), then deleted. */
    photo: IdCheckPhotoUploadSchema.optional(),
})
    .strict()
    .openapi('RecordIdCheck', {
    description: 'What the staff member saw: the document type and the last four characters of its number. Nothing else ' +
        'of the number is accepted — an unknown field is refused, not ignored.',
});
// -------------------------------------------------------------------------------------------------
// The check, as the ID checks tab reads it (spec/13 §8)
// -------------------------------------------------------------------------------------------------
export const IdCheckVerifierSchema = z
    .object({
    /** The staff member's name as they go by now, or `null` when they have not given one. */
    name: z.string().nullable(),
})
    .openapi('IdCheckVerifier', { description: 'Who checked the document.' });
export const IdCheckPhotoSchema = z
    .object({
    contentType: ImageContentTypeSchema,
    /**
     * When the retention job deleted it, `compliance.idPhotoRetentionDays` after the check; `null`
     * while it is kept. A deleted photo cannot be read back — the check itself stays.
     */
    deletedAt: IsoDateTimeSchema.nullable(),
})
    .openapi('IdCheckPhoto', { description: 'The photo taken with a check.' });
export const IdCheckDocumentSchema = z
    .object({
    type: IdDocumentTypeSchema,
    /** The last four characters of its number, upper-cased. Nothing more of it exists anywhere. */
    last4: z.string(),
})
    .openapi('IdCheckDocument', { description: 'The document a staff member looked at.' });
export const IdCheckSchema = z
    .object({
    id: IdCheckIdSchema,
    organizationId: OrganizationIdSchema,
    playerId: PlayerIdSchema,
    method: IdCheckMethodSchema,
    /** What was looked at, for a `staff_document` check; `null` for a digital verification. */
    document: IdCheckDocumentSchema.nullable(),
    /** The member who checked, for a `staff_document` check; `null` for a digital verification. */
    verifiedBy: IdCheckVerifierSchema.nullable(),
    verifiedAt: IsoDateTimeSchema,
    /** `null` when no photo was taken. */
    photo: IdCheckPhotoSchema.nullable(),
})
    .openapi('IdCheck', {
    description: 'One verification of a player, as this organisation sees it: its own staff document checks. Never another ' +
        'organisation’s (spec/07 §10).',
});
export const IdCheckListSchema = paginated(IdCheckSchema, 'IdCheckList');
export const IdCheckPhotoLinkSchema = z
    .object({
    /** `GET /v1/files/{fileId}?…`: the decrypted image, for `ID_PHOTO_URL_TTL_SECONDS`. Use it at once. */
    url: z.url(),
    expiresAt: IsoDateTimeSchema,
    contentType: ImageContentTypeSchema,
})
    .openapi('IdCheckPhotoLink', { description: 'A short-lived signed URL to one ID photo.' });
// -------------------------------------------------------------------------------------------------
// Routes
// -------------------------------------------------------------------------------------------------
/** The header a replayed create answers with (spec/08 §2.2). */
export const IDEMPOTENT_REPLAYED_HEADER = 'Idempotent-Replayed';
export const IdempotentCreateHeaderSchema = z
    .object({
    'idempotency-key': IdempotencyKeySchema,
})
    .openapi('IdempotentCreateHeaders');
/**
 * The `Idempotency-Key` on a counter write that moves money, time or orders — a sale, a payment, a shift's open and
 * close, the day close, a comp (spec/08 §2.2; F-POS-09, F-POS-12, F-OPS-01/-03/-06, F-SES-16). The server stores the
 * key with the answer it gave: a retry with the same key and body is answered from it (`Idempotent-Replayed: true`)
 * and posts nothing twice; the same key with a different request is `422 idempotency-key-reused`. Optional so an
 * older client keeps working; every Clavian client sends one.
 */
export const IdempotencyHeaderSchema = z
    .object({
    'idempotency-key': IdempotencyKeySchema.optional(),
})
    .openapi('IdempotencyHeaders');
const IdCheckPlayerParamsSchema = OrganizationScopeParamsSchema.extend({ playerId: PlayerIdSchema }).openapi('IdCheckPlayerParams');
const IdCheckParamsSchema = IdCheckPlayerParamsSchema.extend({ idCheckId: IdCheckIdSchema }).openapi('IdCheckParams');
export const SignedFileParamsSchema = z.object({ fileId: FileIdSchema }).openapi('SignedFileParams');
export const SignedFileQuerySchema = z
    .object({
    organizationId: OrganizationIdSchema,
    /** Unix seconds. After it, the URL is refused. */
    expires: z.coerce.number().int().positive(),
    /** base64url HMAC over the file, the organisation and `expires`, under the deployment's secret. */
    signature: z.string().min(1).max(128),
})
    .strict()
    .openapi('SignedFileQuery');
const forbidden = problemResponse('Your role does not allow this: it needs `players:id_check`.', 'forbidden', 'second-factor-required');
const noSuchPlayer = problemResponse('No such organisation or player, or not one you can see.', 'not-found');
export const recordIdCheckRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/players/{playerId}/id-checks',
    operationId: 'recordIdCheck',
    tags: ['players'],
    summary: 'Record a staff document ID check',
    description: 'The member looked at a physical ID: records its type, the last four characters of its number and, optionally, ' +
        'a photo of it, which is encrypted into the private bucket and deleted after `compliance.idPhotoRetentionDays`. ' +
        'The check counts for this organisation only. Needs `players:id_check` and an `Idempotency-Key`: a retry with the ' +
        'same key and body answers the check already made (`201`, `Idempotent-Replayed: true`) and records nothing twice. ' +
        'One audit row.',
    [PERMISSION_EXTENSION]: PLAYERS_ID_CHECK,
    request: {
        params: IdCheckPlayerParamsSchema,
        headers: IdempotentCreateHeaderSchema,
        body: { content: { 'application/json': { schema: RecordIdCheckSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(IdCheckSchema, 'The check — new, or the one this key already made.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: noSuchPlayer,
        409: problemResponse('The same key is being used by another request at this moment; send it again.', 'conflict'),
        422: problemResponse('The key was already used for a different check, or the photo is not the image it says it is.', 'idempotency-key-reused', 'unprocessable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const listIdChecksRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/players/{playerId}/id-checks',
    operationId: 'listIdChecks',
    tags: ['players'],
    summary: 'A player’s ID checks at this organisation',
    description: 'Newest first: the staff document checks this organisation made, with who checked and whether a photo is kept. ' +
        'Never another organisation’s. The ID checks tab of the player profile (spec/13 §8). Needs `players:id_check`.',
    [PERMISSION_EXTENSION]: PLAYERS_ID_CHECK,
    request: { params: IdCheckPlayerParamsSchema, query: PageQuerySchema },
    responses: {
        200: jsonResponse(IdCheckListSchema, 'A page of checks.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: noSuchPlayer,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getIdCheckPhotoRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/players/{playerId}/id-checks/{idCheckId}/photo',
    operationId: 'getIdCheckPhoto',
    tags: ['players'],
    summary: 'View an ID check’s photo',
    description: `A signed URL to the photo, valid for ${ID_PHOTO_URL_TTL_SECONDS} seconds. Every call writes one audit row, so ` +
        'ask only when the photo is about to be shown. Needs `players:id_check`. A photo the retention job has deleted, ' +
        'or a check made without one, is `404`.',
    [PERMISSION_EXTENSION]: PLAYERS_ID_CHECK,
    request: { params: IdCheckParamsSchema },
    responses: {
        200: jsonResponse(IdCheckPhotoLinkSchema, 'Where to fetch the photo, for the next minute.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such check, or no photo kept with it.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const readSignedFileRoute = createRoute({
    method: 'get',
    path: '/v1/files/{fileId}',
    operationId: 'readSignedFile',
    tags: ['files'],
    summary: 'Fetch a private file through its signed URL',
    description: 'The URL `getIdCheckPhoto` answers, used as it is — an `<img src>`, say. No session: the signature, over the ' +
        `file, its organisation and the expiry, is the authority, and it lapses ${ID_PHOTO_URL_TTL_SECONDS} seconds after ` +
        'it was issued. Answers the decrypted image with `Cache-Control: no-store`. Not a call a client makes by itself.',
    request: { params: SignedFileParamsSchema, query: SignedFileQuerySchema },
    responses: {
        200: {
            description: 'The image.',
            content: {
                'image/jpeg': { schema: z.string().openapi({ format: 'binary' }) },
                'image/png': { schema: z.string().openapi({ format: 'binary' }) },
                'image/webp': { schema: z.string().openapi({ format: 'binary' }) },
            },
        },
        400: commonProblemResponses[400],
        403: problemResponse('The signature is wrong or has expired.', 'forbidden'),
        404: problemResponse('No such file, or it has been deleted.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const ID_CHECK_ROUTES = [recordIdCheckRoute, listIdChecksRoute, getIdCheckPhotoRoute, readSignedFileRoute];
//# sourceMappingURL=id-checks.js.map